import React, { memo, useState } from 'react';
import styles from './SidebarItem.module.css';

const SidebarItem = memo(({ item, isActive, expanded, index, onClick }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  const renderIcon = () => {
    switch (item.icon) {
      case 'dashboard':
        return (
          <svg viewBox="0 0 24 24" className={styles.icon}>
            <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z" fill="currentColor" />
          </svg>
        );
      case 'plus':
        return (
          <svg viewBox="0 0 24 24" className={styles.icon}>
            <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" fill="currentColor" />
          </svg>
        );
      case 'chart':
        return (
          <svg viewBox="0 0 24 24" className={styles.icon}>
            <path d="M3.5 18.49l6-6.01 4 4L22 6.92l-1.41-1.41-7.09 7.97-4-4L2 16.99z" fill="currentColor" />
          </svg>
        );
      case 'diagram':
        return (
          <svg viewBox="0 0 24 24" className={styles.icon}>
            <path d="M3,3H9V7H3V3M15,10H21V14H15V10M15,17H21V21H15V17M13,13H7V18H13V13Z" fill="currentColor" />
          </svg>
        );
      case 'library':
        return (
          <svg viewBox="0 0 24 24" className={styles.icon}>
            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        );
      case 'gear':
        return (
          <svg viewBox="0 0 24 24" className={styles.icon}>
            <path d="M12 15.5A3.5 3.5 0 0 1 8.5 12A3.5 3.5 0 0 1 12 8.5a3.5 3.5 0 0 1 3.5 3.5 3.5 3.5 0 0 1-3.5 3.5m7.43-2.53c.04-.32.07-.64.07-.97 0-.33-.03-.65-.07-.97l2.11-1.63c.19-.15.24-.42.12-.64l-2-3.46c-.12-.22-.39-.31-.61-.22l-2.49 1c-.52-.39-1.06-.73-1.69-.98l-.37-2.65A.506.506 0 0 0 14 2h-4c-.25 0-.46.18-.5.42l-.37 2.65c-.63.25-1.17.59-1.69.98l-2.49-1c-.22-.09-.49 0-.61.22l-2 3.46c-.13.22-.07.49.12.64L4.57 11c-.04.32-.07.65-.07.97 0 .33.03.65.07.97L2.46 14.6c-.19.15-.24.42-.12.64l2 3.46c.12.22.39.31.61.22l2.49-1c.52.39 1.06.73 1.69.98l.37 2.65c.04.24.25.42.5.42h4c.25 0 .46-.18.5-.42l.37-2.65c.63-.25 1.17-.59 1.69-.98l2.49 1c.22.09.49 0 .61-.22l2-3.46c.12-.22.07-.49-.12-.64l-2.11-1.63Z" fill="currentColor" />
          </svg>
        );
      case 'shield':
        return (
          <svg viewBox="0 0 24 24" className={styles.icon}>
            <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z" fill="currentColor" />
          </svg>
        );
      case 'link':
        return (
          <svg viewBox="0 0 24 24" className={styles.icon}>
            <path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z" fill="currentColor" />
          </svg>
        );
      case 'checklist':
        // Document-with-check: assessing a document against a reference.
        return (
          <svg viewBox="0 0 24 24" className={styles.icon}>
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm0 2 4 4h-4V4zM8 12h3v2H8v-2zm0 4h3v2H8v-2z" fill="currentColor" />
            <path d="M13.4 18.6 11 16.2l1.2-1.2 1.2 1.2 3-3 1.2 1.2-4.2 4.2z" fill="currentColor" />
          </svg>
        );
      default:
        return null;
    }
  };

  const handleClick = () => {
    onClick();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div className={styles.itemContainer}>
      <button
        className={`${styles.sidebarItem} ${isActive ? styles.active : ''}`}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onMouseEnter={() => !expanded && setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        style={{ animationDelay: `${index * 0.1}s` }}
        aria-label={item.label}
        role="menuitem"
        tabIndex={0}
      >
        <div className={styles.iconContainer}>
          {renderIcon()}
          {isActive && <div className={styles.activeGlow} />}
        </div>

        {expanded && (
          <span className={styles.label}>
            {item.label}
          </span>
        )}

        {/* Magnetic effect indicator */}
        <div className={styles.magneticField} />
      </button>

      {/* Tooltip for collapsed state */}
      {!expanded && showTooltip && (
        <div className={styles.tooltip}>
          <span className={styles.tooltipText}>{item.label}</span>
          <div className={styles.tooltipDescription}>{item.description}</div>
        </div>
      )}
    </div>
  );
});

SidebarItem.displayName = 'SidebarItem';

export default SidebarItem;